import React, { useState, useEffect } from "react";

// components
import Modal, { ModalProps } from "@/shared/Modal";
import Button from "@/shared/Button";

// helpers
import analytics from "@/api/analytics";
import dayjs from "@/helpers/dayjs";
import useUserProfile from "@/hooks/useUserProfile";

// assets
import styles from "@/assets/styles/components/Modals/getting-started-modal.module.scss";


interface IGettingStartedStep extends ModalProps {
  src: string;
  buttonText: string;
  modalHint: string;
  onClick: () => void;
}

type TModalMetaInfo = {
  modalName: string | null;
  modalPoppedAt: string | null;
  modalClosedAt: string | null;
  primaryCtaClickedAt: string | null;
};

function GettingStartedModal({ src, open, buttonText, onClick, modalHint, ...props }: IGettingStartedStep) {
  const [modalMetaInfo, setModalMetaInfo] = useState<TModalMetaInfo>({
    modalName: props.title as string,
    modalPoppedAt: null,
    modalClosedAt: null,
    primaryCtaClickedAt: null
  });

  const { isAuthorized } = useUserProfile();

  // methods
  const handleModalClose = () => {
    isAuthorized && analytics.gettingStartedVideo({
      ...modalMetaInfo,
      modalClosedAt: dayjs().toISOString()
    });

    props.onCancel && props.onCancel();
  }

  const onCtaClick = () => {
    isAuthorized && analytics.gettingStartedVideo({
      ...modalMetaInfo,
      primaryCtaClickedAt: dayjs().toISOString()
    });

    onClick();
  }

  // effects
  useEffect(() => {
    if (open) {
      setModalMetaInfo({
        ...modalMetaInfo,
        modalName: props.title as string,
        modalPoppedAt: dayjs().toISOString()
      });
    }
  }, [open, props.title]);

  return (
    <Modal
      open={open}
      destroyOnClose
      { ...props }
      onCancel={handleModalClose}
      className={styles["locl-getting-started-modal"]}
      footer={<Button onClick={onCtaClick} data-testid="locl-getting-started-modal-cta">{buttonText}</Button>}
    >
      {open && (
        <div className={styles["locl-getting-started-modal__video-container"]}>
          <video
            className={styles["locl-getting-started-modal__video"]}
            playsInline
            controls
            preload="none"
            autoPlay
          >
            <source
              className="embed-responsive-item"
              src={src}
              type="video/mp4"
            />
          </video>
        </div>
      )}
      {
        modalHint && (
          <p className={styles["locl-getting-started-modal__hint"]}>
            {modalHint}
          </p>
        )
      }
    </Modal>
  );
}

export default GettingStartedModal;

